import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

// JarvisChart — charts inside the JARVIS HUD panel.
// Combo / professional charts use Recharts (already a dashboard dep);
// the simpler stat/donut/top-N variants stay hand-rolled SVG to keep
// the cyan-glow HUD aesthetic.
//
// Spec types accepted:
//   { type: "bar",        title, data: [{label, value}], unit?, caption? }
//   { type: "donut",      title, data: [{label, value, color?}], unit?, caption? }
//   { type: "line",       title, data: [{label, value}], unit?, caption? }
//   { type: "stat",       title, value, delta?, unit?, subtitle? }
//   { type: "top_n_bar",  title, data: [{label, value, sublabel?}], unit?, caption? }
//   { type: "combo_bar_line", title, bars: [{label, value}], line: [{label, value}], unit?, caption? }

const CY = "#00E5FF";
const CY_DIM = "#1E5F73";
const CY_GLOW = "rgba(0, 229, 255, 0.55)";
const TXT = "#BEEBFF";
const TXT_DIM = "#5A8FA8";
const FAM = '"JetBrains Mono", ui-monospace, monospace';

const PALETTE = ["#00E5FF", "#7CE6FF", "#39B0E6", "#FF8AC0", "#B58CFF", "#FFB585"];

// ---------------------------------------------------------------------------
// Formatting helpers — visual labels (not TTS — the spoken layer handles that).
function fmtValue(v, unit) {
  if (v == null || Number.isNaN(v)) return "—";
  if (unit === "$") {
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (abs >= 10_000) return `$${(v / 1000).toFixed(0)}K`;
    if (abs >= 1000) return `$${(v / 1000).toFixed(1)}K`;
    return `$${Math.round(v).toLocaleString()}`;
  }
  if (unit === "%") return `${v.toFixed(1)}%`;
  if (Number.isInteger(v)) return v.toLocaleString();
  return v.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function ChartFrame({ title, caption, children }) {
  return (
    <div className="w-full h-full flex flex-col" style={{ fontFamily: FAM }}>
      <div
        className="text-[11px] font-bold uppercase tracking-[0.04em] px-1 pb-2 leading-tight"
        style={{ color: CY, textShadow: `0 0 6px ${CY_GLOW}` }}
      >
        {title}
      </div>
      <div className="flex-1 min-h-0 relative">{children}</div>
      {caption ? (
        <div
          className="text-[9px] tracking-[0.08em] uppercase px-1 pt-2"
          style={{ color: TXT_DIM }}
        >
          {caption}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vertical bar chart — used for monthly sales, daily dispensings, etc.
function BarChart({ data, unit }) {
  const maxV = Math.max(...data.map((d) => d.value || 0), 1);
  const n = Math.max(1, data.length);
  // Give each bar a comfortable minimum width so labels never overlap;
  // if the chart needs more room than the panel provides, the outer
  // wrapper makes it horizontally scrollable (matching dashboard charts).
  const MIN_PER_BAR = 44; // px — fits a "MM-DD" label cleanly
  const minInnerWidth = Math.max(n * MIN_PER_BAR, 280);
  // SVG layout — y axis on left
  const W = 100, H = 100;
  const ML = 0; // already in % units; we let CSS scale.
  const barW = 100 / n;
  return (
    <div className="w-full h-full overflow-x-auto overflow-y-hidden" style={{ WebkitOverflowScrolling: "touch" }}>
      <div
        className="h-full flex flex-col gap-2"
        style={{ minWidth: `${minInnerWidth}px`, width: "100%" }}
      >
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full flex-1 min-h-0">
        <defs>
          <linearGradient id="jarvis-bar" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor={CY_DIM} />
            <stop offset="100%" stopColor={CY} />
          </linearGradient>
          <filter id="jarvis-bar-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="0.6" />
          </filter>
        </defs>
        {/* baseline */}
        <line x1="0" y1={H} x2={W} y2={H} stroke={CY_DIM} strokeWidth="0.4" />
        {/* gridlines at 25/50/75% */}
        {[0.25, 0.5, 0.75].map((p) => (
          <line key={p} x1="0" y1={H * (1 - p)} x2={W} y2={H * (1 - p)} stroke={CY_DIM} strokeWidth="0.15" strokeDasharray="0.6 1.2" />
        ))}
        {data.map((d, i) => {
          const h = ((d.value || 0) / maxV) * (H - 4);
          const x = i * barW + barW * 0.18 + ML;
          const w = barW * 0.64;
          const y = H - h;
          return (
            <g key={`bar-${d.label}-${i}`}>
              <rect x={x} y={y} width={w} height={h} fill="url(#jarvis-bar)" filter="url(#jarvis-bar-glow)" />
              <rect x={x} y={y} width={w} height={h} fill="none" stroke={CY} strokeWidth="0.25" />
            </g>
          );
        })}
      </svg>
      <div className="flex justify-between text-[8px] px-1" style={{ color: TXT_DIM, fontFamily: FAM }}>
        {data.map((d, i) => (
          <div key={`lbl-${d.label}-${i}`} className="text-center flex-1 truncate" title={d.label}>
            {d.label}
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[9px] px-1" style={{ color: TXT, fontFamily: FAM }}>
        {data.map((d, i) => (
          <div key={`val-${d.label}-${i}`} className="text-center flex-1 truncate font-bold" style={{ textShadow: `0 0 4px ${CY_GLOW}` }}>
            {fmtValue(d.value, unit)}
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Donut — HCV vs Non-HCV, payer mix, etc. Renders concentric arcs.
function DonutChart({ data, unit }) {
  const total = data.reduce((s, d) => s + (d.value || 0), 0) || 1;
  const cx = 50, cy = 50, rOuter = 38, rInner = 22;
  let angle = -Math.PI / 2; // start at 12 o'clock
  const slices = data.map((d, i) => {
    const frac = (d.value || 0) / total;
    const sweep = frac * Math.PI * 2;
    const a0 = angle;
    const a1 = angle + sweep;
    angle = a1;
    const color = d.color || PALETTE[i % PALETTE.length];
    // SVG arc path
    const x0o = cx + rOuter * Math.cos(a0);
    const y0o = cy + rOuter * Math.sin(a0);
    const x1o = cx + rOuter * Math.cos(a1);
    const y1o = cy + rOuter * Math.sin(a1);
    const x0i = cx + rInner * Math.cos(a1);
    const y0i = cy + rInner * Math.sin(a1);
    const x1i = cx + rInner * Math.cos(a0);
    const y1i = cy + rInner * Math.sin(a0);
    const large = sweep > Math.PI ? 1 : 0;
    const path = [
      `M ${x0o} ${y0o}`,
      `A ${rOuter} ${rOuter} 0 ${large} 1 ${x1o} ${y1o}`,
      `L ${x0i} ${y0i}`,
      `A ${rInner} ${rInner} 0 ${large} 0 ${x1i} ${y1i}`,
      "Z",
    ].join(" ");
    return { ...d, color, frac, path };
  });
  return (
    <div className="w-full h-full flex items-center gap-3">
      <svg viewBox="0 0 100 100" className="h-full" style={{ minWidth: 110 }}>
        <defs>
          <filter id="jarvis-donut-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="0.5" />
          </filter>
        </defs>
        {slices.map((s, i) => (
          <path key={`slice-${s.label}-${i}`} d={s.path} fill={s.color} opacity={0.92} filter="url(#jarvis-donut-glow)" />
        ))}
        {/* inner ring outline */}
        <circle cx={cx} cy={cy} r={rInner - 0.5} fill="none" stroke={CY_DIM} strokeWidth="0.3" />
        <circle cx={cx} cy={cy} r={rOuter + 0.5} fill="none" stroke={CY_DIM} strokeWidth="0.3" />
      </svg>
      <div className="flex-1 min-w-0 space-y-1.5" style={{ fontFamily: FAM }}>
        {slices.map((s, i) => (
          <div key={`legend-${s.label}-${i}`} className="flex items-center gap-1.5 text-[10px]">
            <span className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: s.color, boxShadow: `0 0 6px ${s.color}` }} />
            <span className="truncate flex-1" style={{ color: TXT }}>{s.label}</span>
            <span className="font-bold" style={{ color: CY, textShadow: `0 0 4px ${CY_GLOW}` }}>
              {(s.frac * 100).toFixed(0)}%
            </span>
            <span className="text-[9px] ml-1" style={{ color: TXT_DIM }}>
              {fmtValue(s.value, unit)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Line chart — trends. Uses SVG polyline + dots.
function LineChart({ data, unit }) {
  const values = data.map((d) => d.value || 0);
  const maxV = Math.max(...values, 1);
  const minV = Math.min(...values, 0);
  const range = maxV - minV || 1;
  const n = Math.max(1, data.length);
  const W = 100, H = 100;
  const stepX = n > 1 ? W / (n - 1) : 0;
  const points = data.map((d, i) => {
    const x = i * stepX;
    const y = H - ((d.value - minV) / range) * (H - 4) - 2;
    return [x, y];
  });
  const polyStr = points.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
  // Area under the curve
  const areaStr = `${polyStr} ${W},${H} 0,${H}`;
  return (
    <div className="w-full h-full flex flex-col gap-2">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full flex-1 min-h-0">
        <defs>
          <linearGradient id="jarvis-line-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={CY} stopOpacity="0.45" />
            <stop offset="100%" stopColor={CY} stopOpacity="0" />
          </linearGradient>
          <filter id="jarvis-line-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="0.7" />
          </filter>
        </defs>
        <line x1="0" y1={H} x2={W} y2={H} stroke={CY_DIM} strokeWidth="0.4" />
        {[0.25, 0.5, 0.75].map((p) => (
          <line key={p} x1="0" y1={H * (1 - p)} x2={W} y2={H * (1 - p)} stroke={CY_DIM} strokeWidth="0.15" strokeDasharray="0.6 1.2" />
        ))}
        <polygon points={areaStr} fill="url(#jarvis-line-fill)" />
        <polyline points={polyStr} fill="none" stroke={CY} strokeWidth="0.9" filter="url(#jarvis-line-glow)" strokeLinecap="round" strokeLinejoin="round" />
        {points.map(([x, y], i) => (
          <circle key={`pt-${data[i]?.label || i}-${i}`} cx={x} cy={y} r="1.3" fill="#FFFFFF" stroke={CY} strokeWidth="0.5" />
        ))}
      </svg>
      <div className="flex justify-between text-[8px] px-1" style={{ color: TXT_DIM, fontFamily: FAM }}>
        {data.map((d, i) => (
          <div key={`lab-${d.label}-${i}`} className="text-center flex-1 truncate">
            {d.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single stat card with optional delta.
function StatCard({ value, delta, unit, subtitle }) {
  const valStr = typeof value === "number" ? fmtValue(value, unit) : String(value);
  const deltaPos = typeof delta === "number" && delta > 0;
  const deltaNeg = typeof delta === "number" && delta < 0;
  const deltaColor = deltaPos ? "#39E89B" : deltaNeg ? "#FF7A8E" : TXT_DIM;
  const deltaGlow = deltaPos ? "rgba(57,232,155,0.55)" : deltaNeg ? "rgba(255,122,142,0.55)" : "rgba(0,0,0,0)";
  const arrow = deltaPos ? "▲" : deltaNeg ? "▼" : "•";
  return (
    <div className="w-full h-full flex flex-col items-center justify-center" style={{ fontFamily: FAM }}>
      <div
        className="text-[40px] sm:text-[44px] font-bold leading-none tracking-tight"
        style={{ color: "#FFFFFF", textShadow: `0 0 14px ${CY_GLOW}, 0 0 4px #fff` }}
      >
        {valStr}
      </div>
      {typeof delta === "number" ? (
        <div
          className="mt-2 text-[12px] font-bold tracking-[0.12em]"
          style={{ color: deltaColor, textShadow: `0 0 6px ${deltaGlow}` }}
        >
          {arrow} {Math.abs(delta).toFixed(1)}%
        </div>
      ) : null}
      {subtitle ? (
        <div
          className="mt-1.5 text-[9px] uppercase tracking-[0.18em] text-center"
          style={{ color: TXT_DIM }}
        >
          {subtitle}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top-N horizontal bars — best for top drugs / patients / etc.
function TopNBar({ data, unit }) {
  const maxV = Math.max(...data.map((d) => d.value || 0), 1);
  return (
    <div className="w-full h-full flex flex-col gap-1.5" style={{ fontFamily: FAM }}>
      {data.map((d, i) => {
        const pct = ((d.value || 0) / maxV) * 100;
        return (
          <div key={`top-${d.label}-${i}`} className="flex items-center gap-2 text-[10px]">
            <span className="w-3 text-right" style={{ color: TXT_DIM }}>
              {i + 1}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate" style={{ color: TXT }}>
                  {d.label}
                </span>
                <span className="font-bold flex-shrink-0" style={{ color: CY, textShadow: `0 0 4px ${CY_GLOW}` }}>
                  {fmtValue(d.value, unit)}
                </span>
              </div>
              <div className="mt-0.5 h-1 rounded-sm overflow-hidden" style={{ background: "rgba(0, 229, 255, 0.08)" }}>
                <div
                  className="h-full rounded-sm"
                  style={{
                    width: `${pct}%`,
                    background: `linear-gradient(90deg, ${CY_DIM} 0%, ${CY} 100%)`,
                    boxShadow: `0 0 6px ${CY_GLOW}`,
                  }}
                />
              </div>
              {d.sublabel ? (
                <div className="text-[8px] mt-0.5" style={{ color: TXT_DIM }}>
                  {d.sublabel}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Recharts custom tooltip — hoisted out of ComboBarLine to avoid the
// "component defined during render" React anti-pattern.
function ComboTooltipBox({ active, payload, label, unit }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      className="rounded px-2.5 py-1.5 text-[10px]"
      style={{
        fontFamily: FAM,
        background: "rgba(2, 10, 20, 0.96)",
        color: TXT,
        border: `1px solid ${CY_DIM}`,
        boxShadow: `0 0 12px ${CY_GLOW}`,
      }}
    >
      <div className="font-bold mb-1" style={{ color: CY, textShadow: `0 0 4px ${CY_GLOW}` }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-sm" style={{ background: p.color || p.stroke || p.fill }} />
          <span style={{ color: TXT_DIM }}>{p.dataKey === "bar" ? "Sales" : "Cumulative"}</span>
          <span className="ml-auto font-bold" style={{ color: TXT }}>{fmtValue(p.value, unit)}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Combo bar + cumulative line — Recharts ComposedChart with dual y-axes.
// Bars = period total on the left axis; yellow line with dot markers =
// running cumulative on the right axis. Styled to read clearly inside
// the cyan HUD on a dark panel.
function ComboBarLine({ bars, line, unit }) {
  // Merge into a single Recharts-friendly row array.
  const data = (bars || []).map((b, i) => ({
    label: b.label,
    bar: b.value || 0,
    line: (line && line[i] && line[i].value) || 0,
  }));
  const BAR_STROKE = "#00E5FF";
  const LINE_COLOR = "#FFC83C";
  const fmtAxis = (v) => {
    const abs = Math.abs(v);
    if (unit === "$") {
      if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
      if (abs >= 1000) return `$${Math.round(v / 1000)}K`;
      return `$${Math.round(v)}`;
    }
    if (abs >= 1000) return `${Math.round(v / 1000)}K`;
    return String(Math.round(v));
  };
  // Give each data point a guaranteed minimum slot so x-axis labels
  // never bunch up. If the natural inner-width is less than 100% (few
  // points), the inner div just fills its parent. With many points it
  // overflows and the outer wrapper lets the user pan horizontally.
  const MIN_PER_POINT = 72; // px — comfortable for a 3-letter month label
  const minInnerWidth = Math.max(data.length * MIN_PER_POINT, 320);
  return (
    <div
      className="w-full h-full overflow-x-auto overflow-y-hidden"
      style={{ fontFamily: FAM, WebkitOverflowScrolling: "touch" }}
    >
      <div style={{ minWidth: `${minInnerWidth}px`, width: "100%", height: "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 14, bottom: 4, left: -8 }}>
          <defs>
            <linearGradient id="jarvis-combo-bar-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CY} stopOpacity="0.85" />
              <stop offset="100%" stopColor={CY_DIM} stopOpacity="0.55" />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={CY_DIM} strokeOpacity={0.25} strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: TXT_DIM, fontSize: 10, fontFamily: FAM }}
            axisLine={{ stroke: CY_DIM, strokeOpacity: 0.6 }}
            tickLine={{ stroke: CY_DIM, strokeOpacity: 0.5 }}
            interval={0}
          />
          {/* Single left y-axis shared by bars and the cumulative line — the
              dual-axis version was confusing because the cumulative is
              always taller than any single bar (it's the running sum, so
              that height difference is the truth, not a scale mismatch). */}
          <YAxis
            tick={{ fill: TXT_DIM, fontSize: 9, fontFamily: FAM }}
            axisLine={{ stroke: CY_DIM, strokeOpacity: 0.6 }}
            tickLine={{ stroke: CY_DIM, strokeOpacity: 0.5 }}
            tickFormatter={fmtAxis}
            width={42}
          />
          <Tooltip cursor={{ fill: "rgba(0, 229, 255, 0.08)" }} content={<ComboTooltipBox unit={unit} />} />
          <Legend
            wrapperStyle={{ fontFamily: FAM, fontSize: 10, color: TXT, paddingTop: 4 }}
            iconType="square"
          />
          <Bar
            dataKey="bar"
            name="Sales"
            fill="url(#jarvis-combo-bar-grad)"
            stroke={BAR_STROKE}
            strokeOpacity={0.8}
            strokeWidth={0.5}
            radius={[2, 2, 0, 0]}
            maxBarSize={42}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="line"
            name="Cumulative YTD"
            stroke={LINE_COLOR}
            strokeWidth={2.2}
            dot={{ r: 3.2, stroke: LINE_COLOR, strokeWidth: 1.4, fill: "#0B1422" }}
            activeDot={{ r: 4.5, stroke: LINE_COLOR, strokeWidth: 1.6, fill: "#FFE48A" }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main entry — pick the renderer based on `type`.
export function JarvisChart({ spec }) {
  if (!spec || !spec.type) return null;
  const { type, title, caption, unit } = spec;
  if (type === "bar") {
    return <ChartFrame title={title} caption={caption}><BarChart data={spec.data || []} unit={unit} /></ChartFrame>;
  }
  if (type === "donut") {
    return <ChartFrame title={title} caption={caption}><DonutChart data={spec.data || []} unit={unit} /></ChartFrame>;
  }
  if (type === "line") {
    return <ChartFrame title={title} caption={caption}><LineChart data={spec.data || []} unit={unit} /></ChartFrame>;
  }
  if (type === "stat") {
    return <ChartFrame title={title} caption={caption}>
      <StatCard value={spec.value} delta={spec.delta} unit={unit} subtitle={spec.subtitle} />
    </ChartFrame>;
  }
  if (type === "top_n_bar") {
    return <ChartFrame title={title} caption={caption}><TopNBar data={spec.data || []} unit={unit} /></ChartFrame>;
  }
  if (type === "combo_bar_line") {
    return (
      <ChartFrame title={title} caption={caption}>
        <ComboBarLine bars={spec.bars || []} line={spec.line || []} unit={unit} />
      </ChartFrame>
    );
  }
  return null;
}
